import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ActionMenu.css';

/**
 * ActionMenu component - A dropdown menu for actions in a table row
 * 
 * @param {Object} props - Component props
 * @param {Array} props.actions - Array of action objects with label, onClick, disabled, and className properties
 * @param {string} props.buttonText - Text to display on the main button (optional)
 * @param {string} props.buttonIcon - Icon to display on the main button (optional)
 * @param {string} props.className - Additional class name for the component (optional)
 */
const ActionMenu = ({ actions, buttonText = 'Actions', buttonIcon = '≡', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  
  // Toggle the menu open/closed
  const toggleMenu = (e) => {
    e.stopPropagation();
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    // Calculate dropdown position when opening
    if (newIsOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = actions.length * 40 + 20; // Approximate height
      
      let top, left;
      if (rect.bottom + dropdownHeight > viewportHeight) {
        // Position above
        top = rect.top - dropdownHeight - 5;
      } else {
        // Position below
        top = rect.bottom + 5;
      }
      
      // Align to the right edge of the button
      left = rect.right - 160; // Approximate width
      
      setDropdownPosition({ top, left });
    }
  };
  
  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        // Also check if click is on the portal dropdown
        const dropdown = document.querySelector('.action-menu-dropdown');
        if (!dropdown || !dropdown.contains(event.target)) {
          setIsOpen(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Close the menu when pressing Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  
  return (
    <div className={`action-menu-container ${className}`} ref={menuRef}>
      <button 
        className="action-menu-trigger"
        onClick={toggleMenu}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="action-menu-icon">{buttonIcon}</span>
        <span className="action-menu-text">{buttonText}</span>
      </button>
      
      {isOpen && createPortal(
        <div 
          className="action-menu-dropdown"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 99999
          }}
        >
          {actions.map((action, index) => (
            <button
              key={index}
              className={`action-menu-item ${action.className || ''}`}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(e);
                setIsOpen(false); // Close menu after action
              }}
              disabled={action.disabled}
            >
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionMenu;